import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadAppState, saveAppState, updateUIState } from '../services/persistenceService';

const AppStateContext = createContext();

export const useAppState = () => useContext(AppStateContext);

export const AppStateProvider = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [uiState, setUiState] = useState({});
    const [isRestored, setIsRestored] = useState(false);
    const lastSavedPath = useRef('');
    const [chatbotVisible, setChatbotVisible] = useState(() => {
        const savedState = loadAppState();
        return savedState?.ui?.chatbotVisible !== false; // Default to true
    });

    // Load initial state on mount
    useEffect(() => {
        const savedState = loadAppState();
        if (savedState) {
            setUiState(savedState.ui || {});
            if (savedState.ui?.chatbotVisible !== undefined) {
                setChatbotVisible(savedState.ui.chatbotVisible);
            }
        }
        setIsRestored(true);
    }, []);

    // Sync chatbotVisible to uiState
    useEffect(() => {
        setUiState(prev => ({ ...prev, chatbotVisible }));
    }, [chatbotVisible]);

    // Save route on change
    useEffect(() => {
        if (location.pathname !== '/auth' && location.pathname !== '/landing' && location.pathname !== '/') {
            saveAppState(location.pathname, {}, { ...uiState, chatbotVisible });
            lastSavedPath.current = location.pathname;
        }
    }, [location.pathname, uiState, chatbotVisible]);

    // Fast periodic save for UI state (1 second as requested)
    useEffect(() => {
        const interval = setInterval(() => {
            updateUIState({ ...uiState, chatbotVisible });
        }, 1000);
        return () => clearInterval(interval);
    }, [uiState, chatbotVisible]);

    /**
     * Update a specific component's UI state
     * @param {string} componentKey - Unique key for the component (e.g., 'wallet', 'joinTournament')
     * @param {object} state - The state to merge
     */
    const setComponentState = (componentKey, state) => {
        setUiState(prev => ({
            ...prev,
            [componentKey]: {
                ...(prev[componentKey] || {}),
                ...state
            }
        }));
    };

    /**
     * Get a specific component's UI state
     * @param {string} componentKey 
     * @returns {object}
     */
    const getComponentState = (componentKey) => {
        return uiState[componentKey] || {};
    };

    /**
     * Restore route if one was saved
     */
    const restoreLastRoute = (isAuthenticated) => {
        if (!isAuthenticated) return;

        const savedState = loadAppState();
        if (savedState && savedState.route && savedState.route !== location.pathname) {
            // Check if route is protected or valid
            const protectedRoutes = ['/home', '/my-matches', '/view-details', '/wallet', '/leaderboard', '/profile', '/notifications', '/join-tournament'];
            const isValidRoute = protectedRoutes.some(r => savedState.route.startsWith(r));

            if (isValidRoute) {
                console.log('Restoring last route:', savedState.route);

                // If we are currently on /auth or /, we definitely want to restore
                if (location.pathname === '/auth' || location.pathname === '/') {
                    navigate(savedState.route);
                }
            }
        }
    };

    // Restore scroll position when route matches saved route
    useEffect(() => {
        const savedState = loadAppState();
        if (savedState && savedState.route === location.pathname && savedState.ui && typeof savedState.ui.scrollY === 'number') {
            const scrollAction = () => {
                window.scrollTo({
                    top: savedState.ui.scrollY,
                    behavior: 'instant'
                });
            };

            // Try immediately
            scrollAction();

            // Also try after a short delay for dynamic content
            const timer = setTimeout(scrollAction, 100);
            const timer2 = setTimeout(scrollAction, 500); // For heavier pages

            return () => {
                clearTimeout(timer);
                clearTimeout(timer2);
            };
        }
    }, [location.pathname]);

    return (
        <AppStateContext.Provider value={{
            uiState,
            setComponentState,
            getComponentState,
            restoreLastRoute,
            setUiState,
            chatbotVisible,
            setChatbotVisible
        }}>
            {children}
        </AppStateContext.Provider>
    );
};
